import { API_BASE_URL } from "./advancedSettings";

export type VerificationResult =
  | { status: "sent" }
  | { status: "already-verified" }
  | { status: "wait"; retryAfterSeconds: number }
  | { status: "error" };

/**
 * Asks the API to email the signed-in user a link to confirm their address. The API reads the recipient from the login, so
 * only the token is sent. It answers 429 with a wait time when asked too often, and says when the address is already confirmed.
 * Never throws: the caller shows the matching message.
 */
export async function requestVerificationEmail(getToken: () => Promise<string>): Promise<VerificationResult> {
  try {
    const response = await fetch(`${API_BASE_URL}/email/SendVerification`, {
      method: "POST",
      headers: { Authorization: `Bearer ${await getToken()}` },
    });
    if (response.status === 429) {
      const body = await response.json().catch(() => ({}));
      return { status: "wait", retryAfterSeconds: Number(body.retryAfterSeconds) || 60 };
    }
    if (!response.ok) return { status: "error" };
    const body = await response.json().catch(() => ({}));
    return body.alreadyVerified ? { status: "already-verified" } : { status: "sent" };
  } catch (error) {
    console.error("Could not request the verification email:", error);
    return { status: "error" };
  }
}

// Remembers (for this browser tab) that the signup already sent the email, so the verify screen says so instead of offering to send one.
const SENT_KEY = "verification_email_sent_for";

export function rememberVerificationSent(uid: string): void {
  try { sessionStorage.setItem(SENT_KEY, uid); } catch { /* storage blocked: the screen just offers to send */ }
}

export function wasVerificationSent(uid: string): boolean {
  try { return sessionStorage.getItem(SENT_KEY) === uid; } catch { return false; }
}
